import { injectable } from 'tsyringe';
import type { MeteringCategory, PlanSummary } from '@bmai/shared';
import { authenticatedFetch } from '@/shared/auth/AuthenticatedFetch';

export type PlanUpsert = Omit<PlanSummary, 'id'>;

export interface QuotaBucket {
    category: MeteringCategory;
    limit: number | null;
    used: number;
    overage: boolean;
}

const BASE = '/_billing/api/operator/billing';

async function readError(res: Response, fallback: string): Promise<string> {
    try {
        const body = (await res.json()) as { error?: string };
        if (body.error) return body.error;
    } catch { /* keep fallback */ }
    return fallback;
}

/** Operator plan catalog + per-tenant billing (SAAS Plans & Billing) via the BFF. */
@injectable()
export class OperatorPlansService {
    async list(): Promise<PlanSummary[]> {
        const res = await authenticatedFetch(`${BASE}/plans`);
        if (!res.ok) throw new Error(await readError(res, `Failed to load plans (${res.status})`));
        return (await res.json()) as PlanSummary[];
    }

    async create(input: PlanUpsert): Promise<void> {
        const res = await authenticatedFetch(`${BASE}/plans`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
        });
        if (!res.ok) throw new Error(await readError(res, `Failed to create plan (${res.status})`));
    }

    async update(id: string, input: PlanUpsert): Promise<void> {
        const res = await authenticatedFetch(`${BASE}/plans/${encodeURIComponent(id)}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
        });
        if (!res.ok) throw new Error(await readError(res, `Failed to save plan (${res.status})`));
    }

    async remove(id: string): Promise<void> {
        const res = await authenticatedFetch(`${BASE}/plans/${encodeURIComponent(id)}`, { method: 'DELETE' });
        if (!res.ok) throw new Error(await readError(res, `Failed to delete plan (${res.status})`));
    }

    async assignPlan(tenantId: string, planId: string): Promise<void> {
        const res = await authenticatedFetch(`${BASE}/tenants/${encodeURIComponent(tenantId)}/assign-plan`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ planId }),
        });
        if (!res.ok) throw new Error(await readError(res, `Failed to assign plan (${res.status})`));
    }

    async getTenantQuotas(tenantId: string): Promise<QuotaBucket[]> {
        const res = await authenticatedFetch(`${BASE}/tenants/${encodeURIComponent(tenantId)}/quotas`);
        if (!res.ok) throw new Error(await readError(res, `Failed to load quotas (${res.status})`));
        return (await res.json()) as QuotaBucket[];
    }
}
